import axios, { AxiosResponse } from 'axios';
import { FilterItem } from '../../types/blog';
import {
  APIError,
  CacheEntry,
  PostsParams,
  WordPressApiResponse,
  WordPressPost,
  WordPressTag,
} from '../../types/wordpress';
import { devLog } from '../../utils';

export class WordPressAPIService {
  private readonly baseUrl: string;
  private readonly originalUrl: string;
  private readonly originalParams: URLSearchParams;
  private readonly cache: Map<string, CacheEntry<any>> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 1000; // 1 second

  constructor(fullUrl: string) {
    this.originalUrl = fullUrl;

    // Handle empty URL case
    if (!fullUrl) {
      this.originalParams = new URLSearchParams();
      this.baseUrl = '/';
      return;
    }

    // Parse the original URL to extract base API URL and preserve parameters
    try {
      const url = new URL(fullUrl);
      this.originalParams = new URLSearchParams(url.search);
    } catch (error) {
      // Fallback for invalid URLs
      this.originalParams = new URLSearchParams();
    }

    // Extract base API URL (everything up to /v2/)
    if (fullUrl.includes('/v2/')) {
      this.baseUrl = fullUrl.substring(0, fullUrl.indexOf('/v2/') + 4);
    } else {
      this.baseUrl = fullUrl.endsWith('/') ? fullUrl : fullUrl + '/';
    }
  }

  /**
   * Get posts with enhanced error handling and caching
   */
  async getPosts(params: PostsParams = {}): Promise<WordPressApiResponse<WordPressPost[]>> {
    const url = this.buildPostsUrl(params);
    const cacheKey = this.getCacheKey('posts', params);

    // Check cache first
    const cached = this.getFromCache<WordPressApiResponse<WordPressPost[]>>(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const response = await this.makeRequest<WordPressPost[]>(url);
      const result: WordPressApiResponse<WordPressPost[]> = {
        data: response.data,
        totalPosts: response.headers['x-wp-total'] ?? '0',
        totalPages: response.headers['x-wp-totalpages'] ?? '1',
      };

      // Cache the result
      this.setCache(cacheKey, result);
      return result;
    } catch (error) {
      throw this.handleApiError(error, 'getPosts');
    }
  }

  /**
   * Get featured posts with enhanced logic
   */
  async getFeaturedPosts(): Promise<WordPressPost[]> {
    try {
      // First get the featured tag ID
      const featuredTagIds = await this.getFeaturedTagId();

      if (featuredTagIds.length === 0) {
        return [];
      }

      // Build URL for featured posts
      const params: PostsParams = {
        tags: featuredTagIds[0].toString(),
        _embed: true,
        order: 'desc',
      };

      const response = await this.getPosts(params);
      return response.data;
    } catch (error) {
      devLog.warn('Failed to load featured posts:', error);
      return [];
    }
  }

  /**
   * Get tags that are actually used in posts within the current category
   */
  async getTags(): Promise<FilterItem[]> {
    const cacheKey = this.getCacheKey('tags', this.originalParams.toString());

    // Check cache first
    const cached = this.getFromCache<FilterItem[]>(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      // First, get all posts in the current category to find which tags are actually used
      const postsParams: PostsParams = {
        per_page: 100, // Get enough posts to capture all possible tags
        _fields: 'tags', // Only fetch tag data to minimize payload
      };

      // Add category filter from original URL if present
      if (this.originalParams.has('categories')) {
        postsParams.categories = this.originalParams.get('categories')!;
      }

      const postsResponse = await this.getPosts(postsParams);

      // Extract unique tag IDs from all posts
      const usedTagIds = new Set<number>();
      postsResponse.data.forEach((post) => {
        if (post.tags && Array.isArray(post.tags)) {
          post.tags.forEach((tagId) => usedTagIds.add(tagId));
        }
      });

      if (usedTagIds.size === 0) {
        return [];
      }

      // Now fetch only the tags that are actually used
      const tagIdsArray = Array.from(usedTagIds);
      const url = `${this.baseUrl}tags?include=${tagIdsArray.join(',')}&per_page=100`;
      const response = await this.makeRequest<WordPressTag[]>(url);

      const result = response.data.map((tag) => ({
        id: tag.id,
        name: tag.name,
      }));

      // Cache the result
      this.setCache(cacheKey, result);
      return result;
    } catch (error) {
      throw this.handleApiError(error, 'getTags');
    }
  }

  /**
   * Get featured tag ID
   */
  async getFeaturedTagId(): Promise<number[]> {
    const cacheKey = this.getCacheKey('featured-tag', {});

    // Check cache first
    const cached = this.getFromCache<number[]>(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const url = `${this.baseUrl}tags?search=featured`;
      const response = await this.makeRequest<WordPressTag[]>(url);

      const result = response.data.filter((tag) => tag.name === 'featured').map((tag) => tag.id);

      // Cache the result
      this.setCache(cacheKey, result);
      return result;
    } catch (error) {
      throw this.handleApiError(error, 'getFeaturedTagId');
    }
  }

  // Private helper methods
  private buildPostsUrl(params: PostsParams): string {
    const basePostsUrl = `${this.baseUrl}posts`;

    // Start with original URL parameters (like categories=4)
    const searchParams = new URLSearchParams(this.originalParams);

    // Add default parameters
    searchParams.set('_embed', 'true');

    // Add/override with provided parameters
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.set(key, value.toString());
      }
    });

    const finalUrl = `${basePostsUrl}?${searchParams.toString()}`;

    return finalUrl;
  }

  private async makeRequest<T>(url: string, retryCount = 0): Promise<AxiosResponse<T>> {
    try {
      const response = await axios.get<T>(url, {
        timeout: 10000, // 10 second timeout
        headers: {
          Accept: 'application/json',
        },
      });

      if (!this.isValidResponse(response)) {
        throw new Error('Invalid response format');
      }

      return response;
    } catch (error) {
      if (retryCount < this.MAX_RETRIES && this.isRetryableError(error)) {
        await this.delay(this.RETRY_DELAY * (retryCount + 1));
        return this.makeRequest(url, retryCount + 1);
      }
      throw error;
    }
  }

  private isValidResponse(response: AxiosResponse): boolean {
    return response && response.status >= 200 && response.status < 300 && response.data;
  }

  private isRetryableError(error: any): boolean {
    if (!error.response) {
      return true; // Network error, retry
    }

    const status = error.response.status;
    return status >= 500 || status === 429; // Server error or rate limit
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private getCacheKey(operation: string, params: any): string {
    const paramString = JSON.stringify(params);
    return `${operation}:${btoa(paramString)}`;
  }

  private getFromCache<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) {
      return null;
    }

    if (entry.expiresAt < Date.now()) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  private setCache<T>(key: string, data: T): void {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + this.CACHE_DURATION,
    };

    this.cache.set(key, entry);

    // Periodically clean up expired entries
    if (this.cache.size > 100) {
      this.clearExpiredCacheInternal();
    }
  }

  private clearExpiredCacheInternal(): void {
    const now = Date.now();
    const entries = Array.from(this.cache.entries());
    for (const [key, entry] of entries) {
      if (entry.expiresAt < now) {
        this.cache.delete(key);
      }
    }
  }

  private handleApiError(error: any, operation: string): APIError {
    const apiError: APIError = {
      message: this.getErrorMessage(error),
      operation,
      originalError: error,
      timestamp: new Date(),
    };

    // Log error for debugging
    devLog.error(`WordPress API Error in ${operation}:`, apiError);

    return apiError;
  }

  private getErrorMessage(error: any): string {
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const data = error.response.data;

      if (status === 404) {
        return 'Content not found';
      } else if (status === 429) {
        return 'Too many requests, please try again later';
      } else if (status >= 500) {
        return 'Server error, please try again later';
      } else if (data?.message) {
        return data.message;
      }

      return `HTTP ${status}: ${error.response.statusText}`;
    } else if (error.request) {
      // Network error
      return 'Network error, please check your connection';
    } else {
      // Other error
      return error.message ?? 'An unexpected error occurred';
    }
  }
}
